/**
 * "Look ahead" for Total Scheduled Revenue: a few extra days drawn past the end
 * of the period, so the first days of next month can be seen while this month
 * is still running (on Sept 30, the work already booked for Oct 1-5).
 *
 * Kept as a tiny pure module (no hooks, no redux) so the editor, the settings
 * sanitiser and the data hook can all share the one list of allowed values.
 */

/** How many days ahead an admin can ask for. 0 (off) is the default. */
export const LOOK_AHEAD_OPTIONS = [3, 5, 7];

/** Chart styles that can carry the extra days. Sparkline has no axis to hang them on. */
export const LOOK_AHEAD_STYLES = ["bar", "area", "line"];

/** A stored setting as a safe number of days: one of the allowed values, else 0. */
export const normalizeLookAhead = (value) => {
  const n = Number(value);
  return LOOK_AHEAD_OPTIONS.includes(n) ? n : 0;
};

/** True for the metric's name as the metric picker spells it. */
export const isScheduledRevenueName = (name) => (
  String(name || "").trim().toLowerCase() === "total scheduled revenue"
);

/**
 * How many days ahead this card actually draws.
 *
 * Only Total Scheduled Revenue, only in the "That day only" view, and only on
 * a chart style that can show them. A running total would keep climbing through
 * the extra days and end up above the number over the chart and the goal, which
 * cover the period alone, so it never looks ahead.
 *
 * @param {object} settings  the widget's saved options
 * @param {boolean} isScheduledPick
 */
export const lookAheadDaysFor = (settings, isScheduledPick) => {
  if (!isScheduledPick) return 0;
  const s = settings || {};
  if (s.chartView !== "daily") return 0;
  if (!LOOK_AHEAD_STYLES.includes(s.chartStyle || "area")) return 0;
  return normalizeLookAhead(s.lookAhead);
};

/**
 * Where the chart's calendar ends. Only a period that has not finished yet
 * (its end is today or later) looks ahead: a finished month's "next days" are
 * simply the current month, and adding them would draw work that belongs to a
 * different card's period.
 *
 * @param {moment.Moment} periodEnd  the period's own last day
 * @param {number} aheadDays         already normalised
 * @param {moment.Moment} today      the company's today
 */
export const lookAheadEnd = (periodEnd, aheadDays, today) => {
  if (!(aheadDays > 0) || !periodEnd || !periodEnd.isValid() || !today) return periodEnd;
  if (!periodEnd.isSameOrAfter(today, "day")) return periodEnd;
  return periodEnd.clone().add(aheadDays, "days");
};
